/**
 * Unit tables for the field-and-force calculators.
 *
 * Every family converts through its SI base unit (kg, m, N, s, …); `factor` is the size
 * of one unit in that base. Factors are exact definitions or the values they are quoted
 * from:
 *
 * - avoirdupois pound = 0.453 592 37 kg and foot = 0.3048 m (1959 international yard and
 *   pound agreement), so 1 lbf = 0.453 592 37 × 9.806 65 = 4.448 221 615 260 5 N exactly;
 * - mile = 1609.344 m, inch = 0.0254 m, US gallon = 3.785 411 784 L (NIST SP 811);
 * - astronomical unit = 149 597 870 700 m (IAU 2012), light-year = c × 365.25 days;
 * - TNT equivalent: 1 tonne of TNT = 4.184 × 10⁹ J by convention; BTU is the
 *   International Table BTU, 1055.055 852 62 J; calorie is the thermochemical 4.184 J.
 *
 * Unit symbols are not translated, so they live here as data rather than in a widget's
 * strings.
 */

import {
  ASTRONOMICAL_UNIT,
  ATOMIC_MASS_UNIT,
  ELEMENTARY_CHARGE,
  LIGHT_YEAR,
  SPEED_OF_LIGHT,
  STANDARD_ATMOSPHERE,
  STANDARD_GRAVITY,
  TNT_TON_JOULES,
} from "@/lib/tool-kit/physics-fields/constants";

export interface UnitDef {
  id: string;
  /** What the reader sees and how the unit is written in a formula. */
  symbol: string;
  /** Size of one unit in the family's SI base unit. */
  factor: number;
  /**
   * Base value of zero in this unit, for scales that do not start at zero: base =
   * value × factor + offset. Only temperatures use it.
   */
  offset?: number;
}

const POUND = 0.453_592_37;
const FOOT = 0.3048;
const INCH = 0.0254;
const LBF = POUND * STANDARD_GRAVITY;

export const MASS_UNITS: readonly UnitDef[] = [
  { id: "kg", symbol: "kg", factor: 1 },
  { id: "g", symbol: "g", factor: 1e-3 },
  { id: "mg", symbol: "mg", factor: 1e-6 },
  { id: "t", symbol: "t", factor: 1e3 },
  { id: "lb", symbol: "lb", factor: POUND },
  { id: "oz", symbol: "oz", factor: POUND / 16 },
];

export const LENGTH_UNITS: readonly UnitDef[] = [
  { id: "m", symbol: "m", factor: 1 },
  { id: "mm", symbol: "mm", factor: 1e-3 },
  { id: "cm", symbol: "cm", factor: 1e-2 },
  { id: "km", symbol: "km", factor: 1e3 },
  { id: "in", symbol: "in", factor: INCH },
  { id: "ft", symbol: "ft", factor: FOOT },
  { id: "mi", symbol: "mi", factor: 1609.344 },
];

/** Lengths from metres out to light-years, for orbits and relativity. */
export const DISTANCE_UNITS: readonly UnitDef[] = [
  { id: "m", symbol: "m", factor: 1 },
  { id: "km", symbol: "km", factor: 1e3 },
  { id: "mi", symbol: "mi", factor: 1609.344 },
  { id: "AU", symbol: "AU", factor: ASTRONOMICAL_UNIT },
  { id: "ly", symbol: "ly", factor: LIGHT_YEAR },
];

export const FORCE_UNITS: readonly UnitDef[] = [
  { id: "N", symbol: "N", factor: 1 },
  { id: "mN", symbol: "mN", factor: 1e-3 },
  { id: "kN", symbol: "kN", factor: 1e3 },
  { id: "dyn", symbol: "dyn", factor: 1e-5 },
  { id: "lbf", symbol: "lbf", factor: LBF },
  { id: "kgf", symbol: "kgf", factor: STANDARD_GRAVITY },
];

export const TIME_UNITS: readonly UnitDef[] = [
  { id: "s", symbol: "s", factor: 1 },
  { id: "ms", symbol: "ms", factor: 1e-3 },
  { id: "min", symbol: "min", factor: 60 },
  { id: "h", symbol: "h", factor: 3600 },
  { id: "d", symbol: "d", factor: 86_400 },
  { id: "yr", symbol: "yr", factor: 365.25 * 86_400 },
];

export const SPEED_UNITS: readonly UnitDef[] = [
  { id: "m/s", symbol: "m/s", factor: 1 },
  { id: "km/s", symbol: "km/s", factor: 1e3 },
  { id: "km/h", symbol: "km/h", factor: 1 / 3.6 },
  { id: "mph", symbol: "mph", factor: 0.447_04 },
  { id: "ft/s", symbol: "ft/s", factor: FOOT },
  { id: "c", symbol: "c", factor: SPEED_OF_LIGHT },
];

/** Speeds for relativity: as a fraction or a percentage of c, and everyday units. */
export const RELATIVISTIC_SPEED_UNITS: readonly UnitDef[] = [
  { id: "c", symbol: "c", factor: SPEED_OF_LIGHT },
  { id: "%c", symbol: "% of c", factor: SPEED_OF_LIGHT / 100 },
  { id: "km/s", symbol: "km/s", factor: 1e3 },
  { id: "m/s", symbol: "m/s", factor: 1 },
  { id: "km/h", symbol: "km/h", factor: 1 / 3.6 },
  { id: "mph", symbol: "mph", factor: 0.447_04 },
];

export const AREA_UNITS: readonly UnitDef[] = [
  { id: "m2", symbol: "m²", factor: 1 },
  { id: "cm2", symbol: "cm²", factor: 1e-4 },
  { id: "mm2", symbol: "mm²", factor: 1e-6 },
  { id: "km2", symbol: "km²", factor: 1e6 },
  { id: "in2", symbol: "in²", factor: INCH * INCH },
  { id: "ft2", symbol: "ft²", factor: FOOT * FOOT },
];

export const VOLUME_UNITS: readonly UnitDef[] = [
  { id: "m3", symbol: "m³", factor: 1 },
  { id: "L", symbol: "L", factor: 1e-3 },
  { id: "mL", symbol: "mL", factor: 1e-6 },
  { id: "cm3", symbol: "cm³", factor: 1e-6 },
  { id: "in3", symbol: "in³", factor: INCH ** 3 },
  { id: "ft3", symbol: "ft³", factor: FOOT ** 3 },
  { id: "gal", symbol: "gal (US)", factor: 3.785_411_784e-3 },
];

export const DENSITY_UNITS: readonly UnitDef[] = [
  { id: "kg/m3", symbol: "kg/m³", factor: 1 },
  { id: "g/cm3", symbol: "g/cm³", factor: 1000 },
  { id: "kg/L", symbol: "kg/L", factor: 1000 },
  { id: "lb/ft3", symbol: "lb/ft³", factor: POUND / FOOT ** 3 },
];

export const PRESSURE_UNITS: readonly UnitDef[] = [
  { id: "Pa", symbol: "Pa", factor: 1 },
  { id: "kPa", symbol: "kPa", factor: 1e3 },
  { id: "MPa", symbol: "MPa", factor: 1e6 },
  { id: "bar", symbol: "bar", factor: 1e5 },
  { id: "atm", symbol: "atm", factor: STANDARD_ATMOSPHERE },
  { id: "psi", symbol: "psi", factor: LBF / INCH ** 2 },
  { id: "mmHg", symbol: "mmHg", factor: 133.322_387_415 },
  { id: "torr", symbol: "Torr", factor: STANDARD_ATMOSPHERE / 760 },
];

export const CHARGE_UNITS: readonly UnitDef[] = [
  { id: "C", symbol: "C", factor: 1 },
  { id: "mC", symbol: "mC", factor: 1e-3 },
  { id: "uC", symbol: "µC", factor: 1e-6 },
  { id: "nC", symbol: "nC", factor: 1e-9 },
  { id: "pC", symbol: "pC", factor: 1e-12 },
  { id: "e", symbol: "e", factor: ELEMENTARY_CHARGE },
];

export const ACCELERATION_UNITS: readonly UnitDef[] = [
  { id: "m/s2", symbol: "m/s²", factor: 1 },
  { id: "ft/s2", symbol: "ft/s²", factor: FOOT },
  { id: "g0", symbol: "g₀", factor: STANDARD_GRAVITY },
];

export const STIFFNESS_UNITS: readonly UnitDef[] = [
  { id: "N/m", symbol: "N/m", factor: 1 },
  { id: "N/cm", symbol: "N/cm", factor: 100 },
  { id: "N/mm", symbol: "N/mm", factor: 1000 },
  { id: "kN/m", symbol: "kN/m", factor: 1000 },
  { id: "lbf/in", symbol: "lbf/in", factor: LBF / INCH },
];

export const FIELD_UNITS: readonly UnitDef[] = [
  { id: "N/C", symbol: "N/C", factor: 1 },
  { id: "V/m", symbol: "V/m", factor: 1 },
  { id: "kN/C", symbol: "kN/C", factor: 1e3 },
  { id: "kV/m", symbol: "kV/m", factor: 1e3 },
  { id: "MN/C", symbol: "MN/C", factor: 1e6 },
];

/** Forces from a pico-newton up, for charges and atoms. */
export const SMALL_FORCE_UNITS: readonly UnitDef[] = [
  { id: "N", symbol: "N", factor: 1 },
  { id: "mN", symbol: "mN", factor: 1e-3 },
  { id: "uN", symbol: "µN", factor: 1e-6 },
  { id: "nN", symbol: "nN", factor: 1e-9 },
  { id: "pN", symbol: "pN", factor: 1e-12 },
  { id: "kN", symbol: "kN", factor: 1e3 },
  { id: "dyn", symbol: "dyn", factor: 1e-5 },
];

/** Distances from kilometres to picometres, including the ångström. */
export const SMALL_DISTANCE_UNITS: readonly UnitDef[] = [
  { id: "m", symbol: "m", factor: 1 },
  { id: "cm", symbol: "cm", factor: 1e-2 },
  { id: "mm", symbol: "mm", factor: 1e-3 },
  { id: "um", symbol: "µm", factor: 1e-6 },
  { id: "nm", symbol: "nm", factor: 1e-9 },
  { id: "A", symbol: "Å", factor: 1e-10 },
  { id: "pm", symbol: "pm", factor: 1e-12 },
  { id: "km", symbol: "km", factor: 1e3 },
];

/** Electric potential. */
export const POTENTIAL_UNITS: readonly UnitDef[] = [
  { id: "V", symbol: "V", factor: 1 },
  { id: "mV", symbol: "mV", factor: 1e-3 },
  { id: "kV", symbol: "kV", factor: 1e3 },
  { id: "MV", symbol: "MV", factor: 1e6 },
];

export const ENERGY_UNITS: readonly UnitDef[] = [
  { id: "J", symbol: "J", factor: 1 },
  { id: "mJ", symbol: "mJ", factor: 1e-3 },
  { id: "kJ", symbol: "kJ", factor: 1e3 },
  { id: "MJ", symbol: "MJ", factor: 1e6 },
  { id: "GJ", symbol: "GJ", factor: 1e9 },
  { id: "TJ", symbol: "TJ", factor: 1e12 },
  { id: "PJ", symbol: "PJ", factor: 1e15 },
  { id: "kWh", symbol: "kWh", factor: 3.6e6 },
  { id: "MWh", symbol: "MWh", factor: 3.6e9 },
  { id: "GWh", symbol: "GWh", factor: 3.6e12 },
  { id: "TWh", symbol: "TWh", factor: 3.6e15 },
  { id: "eV", symbol: "eV", factor: ELEMENTARY_CHARGE },
  { id: "keV", symbol: "keV", factor: ELEMENTARY_CHARGE * 1e3 },
  { id: "MeV", symbol: "MeV", factor: ELEMENTARY_CHARGE * 1e6 },
  { id: "GeV", symbol: "GeV", factor: ELEMENTARY_CHARGE * 1e9 },
  { id: "TeV", symbol: "TeV", factor: ELEMENTARY_CHARGE * 1e12 },
  { id: "cal", symbol: "cal", factor: 4.184 },
  { id: "kcal", symbol: "kcal", factor: 4184 },
  { id: "BTU", symbol: "BTU", factor: 1055.055_852_62 },
  { id: "tTNT", symbol: "t TNT", factor: TNT_TON_JOULES },
  { id: "ktTNT", symbol: "kt TNT", factor: TNT_TON_JOULES * 1e3 },
  { id: "MtTNT", symbol: "Mt TNT", factor: TNT_TON_JOULES * 1e6 },
];

/** Masses for the mass–energy calculator, including particle-physics mass units. */
export const REST_MASS_UNITS: readonly UnitDef[] = [
  { id: "kg", symbol: "kg", factor: 1 },
  { id: "g", symbol: "g", factor: 1e-3 },
  { id: "mg", symbol: "mg", factor: 1e-6 },
  { id: "ug", symbol: "µg", factor: 1e-9 },
  { id: "t", symbol: "t", factor: 1e3 },
  { id: "lb", symbol: "lb", factor: POUND },
  { id: "u", symbol: "u", factor: ATOMIC_MASS_UNIT },
  {
    id: "MeV/c2",
    symbol: "MeV/c²",
    factor: (ELEMENTARY_CHARGE * 1e6) / SPEED_OF_LIGHT ** 2,
  },
  {
    id: "GeV/c2",
    symbol: "GeV/c²",
    factor: (ELEMENTARY_CHARGE * 1e9) / SPEED_OF_LIGHT ** 2,
  },
];

/** The unit with this id, or the family's first unit when the id is unknown. */
export function findUnit(units: readonly UnitDef[], id: string): UnitDef {
  return units.find((unit) => unit.id === id) ?? units[0];
}

export function toBase(
  units: readonly UnitDef[],
  id: string,
  value: number
): number {
  const unit = findUnit(units, id);
  return value * unit.factor + (unit.offset ?? 0);
}

export function fromBase(
  units: readonly UnitDef[],
  id: string,
  value: number
): number {
  const unit = findUnit(units, id);
  return (value - (unit.offset ?? 0)) / unit.factor;
}

/** Temperatures as kelvin-based units: base = value × factor + offset. */
export const TEMPERATURE_UNITS: readonly UnitDef[] = [
  { id: "K", symbol: "K", factor: 1 },
  { id: "C", symbol: "°C", factor: 1, offset: 273.15 },
  { id: "F", symbol: "°F", factor: 5 / 9, offset: 273.15 - 160 / 9 },
  { id: "R", symbol: "°R", factor: 5 / 9 },
];

/** An efficiency or a ratio, as a fraction or a percentage. */
export const EFFICIENCY_UNITS: readonly UnitDef[] = [
  { id: "%", symbol: "%", factor: 0.01 },
  { id: "frac", symbol: "0–1", factor: 1 },
];

/** Celsius, Fahrenheit, Kelvin and Rankine to kelvin, and back. */
export type TemperatureUnit = "K" | "C" | "F" | "R";

export const TEMPERATURE_SYMBOLS: Record<TemperatureUnit, string> = {
  K: "K",
  C: "°C",
  F: "°F",
  R: "°R",
};

export function temperatureToKelvin(
  value: number,
  unit: TemperatureUnit
): number {
  switch (unit) {
    case "C":
      return value + 273.15;
    case "F":
      return ((value - 32) * 5) / 9 + 273.15;
    case "R":
      return (value * 5) / 9;
    default:
      return value;
  }
}

export function kelvinToTemperature(
  kelvin: number,
  unit: TemperatureUnit
): number {
  switch (unit) {
    case "C":
      return kelvin - 273.15;
    case "F":
      return ((kelvin - 273.15) * 9) / 5 + 32;
    case "R":
      return (kelvin * 9) / 5;
    default:
      return kelvin;
  }
}
